// Shared Apple-style building blocks (large titles, inset grouped lists, reveal-on-scroll).
import React, {useCallback, useEffect, useRef} from "react";
import {Link} from "react-router-dom";
import styled, {css} from "styled-components";

export const WHATSAPP_URL = "https://wa.me/972523363515?text=%D7%94%D7%99%D7%99%20%D7%9B%D7%A4%D7%99%D7%A8%20%D7%A8%D7%A6%D7%99%D7%AA%D7%99%20%D7%9C%D7%94%D7%95%D7%A1%D7%99%D7%A3";

/* ------------------------------------------------------------------ Liquid Glass button system
   One family of capsules for the whole site:
     glass      — translucent, for secondary actions on light surfaces
     glassDark  — the same material tuned for colorful / dark artwork
     filled     — the primary action (tint), with the same specular rim
   Press: instant scale-down + a light bloom from the touch point (--px/--py, set by pressLight()). */
const rim = css`
    /* light catching the top edge + a faint inner outline */
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.85),
        inset 0 -0.5px 0.5px rgba(255, 255, 255, 0.35),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.45),
        0 6px 18px rgba(0, 0, 0, 0.1),
        0 1px 2px rgba(0, 0, 0, 0.08);
`;

const press = css`
    position: relative;
    isolation: isolate;
    overflow: hidden;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: transform 380ms var(--ease), filter 200ms ease, background-color 200ms ease;

    &::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: -1;
        border-radius: inherit;
        background: radial-gradient(120px circle at var(--px, 50%) var(--py, 50%), rgba(255, 255, 255, 0.55), transparent 60%);
        opacity: 0;
        transition: opacity 360ms ease;
        pointer-events: none;
    }
    &:active {
        transform: scale(0.95);
        transition-duration: 90ms;
    }
    &:active::after { opacity: 1; transition-duration: 60ms; }
    &:focus-visible { outline: 2px solid var(--tint); outline-offset: 3px; }
    @media (hover: hover) and (pointer: fine) {
        &:hover { filter: brightness(1.06) saturate(1.05); }
    }
`;

export const glass = css`
    ${press}
    color: var(--label);
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.72), rgba(255, 255, 255, 0.42));
    -webkit-backdrop-filter: blur(16px) saturate(190%);
    backdrop-filter: blur(16px) saturate(190%);
    ${rim}
    @media (prefers-reduced-transparency: reduce) {
        background: #fff;
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
    }
`;

export const glassDark = css`
    ${press}
    color: #fff;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.12));
    -webkit-backdrop-filter: blur(14px) saturate(170%);
    backdrop-filter: blur(14px) saturate(170%);
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.6),
        inset 0 0 0 0.5px rgba(255, 255, 255, 0.28),
        0 8px 22px rgba(0, 0, 0, 0.22);
    &::after { background: radial-gradient(120px circle at var(--px, 50%) var(--py, 50%), rgba(255, 255, 255, 0.4), transparent 60%); }
    @media (prefers-reduced-transparency: reduce) {
        background: rgba(30, 30, 34, 0.92);
        -webkit-backdrop-filter: none;
        backdrop-filter: none;
    }
`;

export const filled = css`
    ${press}
    color: #fff;
    background: linear-gradient(180deg, #2f8cff 0%, #0a6cf0 100%);
    box-shadow:
        inset 0 1px 0.5px rgba(255, 255, 255, 0.55),
        inset 0 -1px 1px rgba(0, 40, 120, 0.25),
        0 8px 20px rgba(0, 122, 255, 0.32),
        0 1px 2px rgba(0, 0, 0, 0.12);
`;

export const capsule = css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 44px;
    padding: 0 20px;
    border: none;
    border-radius: 999px;
    font: inherit;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    text-decoration: none;
    white-space: nowrap;
`;

/** Records the touch point so the press light blooms from the finger (one global listener). */
if (typeof document !== 'undefined' && !window.__pressLight) {
    window.__pressLight = true;
    document.addEventListener('pointerdown', (e) => {
        const el = e.target && e.target.closest && e.target.closest('[data-glass]');
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--px', `${e.clientX - r.left}px`);
        el.style.setProperty('--py', `${e.clientY - r.top}px`);
    }, {passive: true, capture: true});
}

/* tiny glyphs used inside buttons */
export const PlayGlyph = () => (
    <svg width="12" height="13" viewBox="0 0 12 13" fill="currentColor" aria-hidden="true"><path d="M2 1.2v10.6a.9.9 0 0 0 1.37.77l8.2-5.3a.9.9 0 0 0 0-1.54l-8.2-5.3A.9.9 0 0 0 2 1.2z"/></svg>
);
export const ArrowGlyph = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M8.5 2.5L4 7l4.5 4.5"/></svg>
);

export const PageHeader = styled.header`
    padding: 12px 4px 18px;
    text-align: right;

    @media (min-width: 768px) {
        padding: 20px 4px 26px;
    }
`;

/* iOS large title: tight leading + negative tracking as it grows */
export const LargeTitle = styled.h1`
    margin: 0;
    font-size: clamp(32px, 5vw, 44px);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.025em;
    color: var(--label);
    text-align: right;
`;

/* apple.com-style centred marketing headline ("Endless entertainment.") */
export const HeroHeadline = ({title, sub}) => (
    <HeroBox>
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
    </HeroBox>
);

const HeroBox = styled.header`
    padding: 28px 20px 26px;
    text-align: center;

    h1 {
        margin: 0;
        font-size: clamp(38px, 6.2vw, 80px);
        font-weight: 800;
        line-height: 1.04;
        letter-spacing: -0.035em;
        color: var(--label);
    }
    p {
        margin: 10px auto 0;
        max-width: 34ch;
        font-size: clamp(17px, 1.8vw, 22px);
        line-height: 1.35;
        color: var(--label-2);
    }
    @media (min-width: 768px) { padding: 44px 20px 34px; }
`;

/* full-width page column (hero carousel spans edge to edge; content sits in Wrap) */
export const Column = styled.div`
    width: 100%;
    min-width: 0;
`;

export const ShelfTitle = styled.h2`
    margin: 40px 4px 14px;
    font-size: clamp(24px, 3vw, 32px);
    font-weight: 800;
    letter-spacing: -0.025em;
    text-align: right;
    color: var(--label);
`;

export const Subtitle = styled.p`
    margin: 6px 0 0;
    font-size: 17px;
    font-weight: 400;
    line-height: 1.4;
    color: var(--label-2);
`;

export const SectionTitle = styled.h2`
    margin: 0 0 8px;
    padding: 0 16px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: var(--label-2);
`;

export const Section = styled.section`
    margin-top: 28px;
`;

/* inset grouped list (Settings-style) */
export const Group = styled.div`
    background: var(--surface);
    border-radius: var(--radius-md);
    overflow: hidden;
    box-shadow: var(--shadow-sm);
`;

export const Row = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 44px;
    padding: 11px 16px;
    position: relative;

    & + &::before {
        content: "";
        position: absolute;
        top: 0;
        right: ${(p) => p.$inset || 16}px;
        left: 0;
        border-top: 0.5px solid var(--separator);
    }
`;

/** Fades/raises children in once as they scroll into view (cross-fade only under reduced motion). */
export const Reveal = ({children, delay = 0, as: Tag = 'div', ...rest}) => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (!('IntersectionObserver' in window)) { el.classList.add('in'); return; }
        const io = new IntersectionObserver((entries) => {
            for (const e of entries) if (e.isIntersecting) { el.classList.add('in'); io.disconnect(); }
        }, {rootMargin: '0px 0px -8% 0px', threshold: 0.05});
        io.observe(el);
        return () => io.disconnect();
    }, []);
    const {style, ...other} = rest;
    return <RevealBox as={Tag} ref={ref} style={{transitionDelay: `${delay}ms`, ...style}} {...other}>{children}</RevealBox>;
};

const RevealBox = styled.div`
    opacity: 0;
    transform: translateY(14px);
    transition: opacity 600ms var(--ease), transform 700ms var(--ease);

    &.in { opacity: 1; transform: none; }

    @media (prefers-reduced-motion: reduce) {
        transform: none;
        transition: opacity 250ms ease;
    }
`;

/* ------------------------------------------------------------------ tvOS-style focus tilt
   Direct manipulation: the tile tracks the pointer 1:1 while it's over it (no easing lag),
   then settles back with the critically damped curve when the pointer leaves.
   Mouse/trackpad only — touch keeps the simple press-scale. */
const canTilt = () =>
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useTilt(max = 6) {
    const onPointerMove = useCallback((e) => {
        if (e.pointerType !== 'mouse' || !canTilt()) return;
        const el = e.currentTarget;
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        el.classList.add('tracking');
        el.style.setProperty('--ry', `${(px - 0.5) * 2 * max}deg`);
        el.style.setProperty('--rx', `${(0.5 - py) * 2 * max}deg`);
        el.style.setProperty('--mx', `${px * 100}%`);
        el.style.setProperty('--my', `${py * 100}%`);
    }, [max]);
    const onPointerLeave = useCallback((e) => {
        const el = e.currentTarget;
        el.classList.remove('tracking');
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
    }, []);
    return {onPointerMove, onPointerLeave};
}

/* ------------------------------------------------------------------ site footer (apple.com-style) */
const FOOTER_LINKS = [
    {to: "/about", label: "מי אני"},
    {to: "/", label: "משחקים"},
    {to: "/family", label: "עץ המשפחה"},
    {to: "/events", label: "אירועים"},
    {to: "/sigal", label: "סיגל בית ספר"},
];

export const SiteFooter = ({contact = true}) => (
    <Foot>
        {contact && (
            <p>
                מצאתם טעות? רוצים להוסיף או לעדכן נתונים?{' '}
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">שלחו לי הודעה בוואטסאפ</a>
            </p>
        )}
        <nav aria-label="קישורים">
            {FOOTER_LINKS.map((l) => <Link key={l.to} to={l.to}>{l.label}</Link>)}
        </nav>
        <small>© {new Date().getFullYear()} כפיר מילר</small>
    </Foot>
);
export const ContactFooter = SiteFooter;

const Foot = styled.footer`
    margin: 48px 4px 8px;
    padding-top: 16px;
    border-top: 0.5px solid var(--separator);
    font-size: 12px;
    line-height: 1.6;
    color: var(--label-2);
    text-align: right;

    p { margin: 0 0 10px; }
    a { color: var(--tint); text-decoration: none; }
    a:active { opacity: 0.55; }
    nav {
        display: flex;
        flex-wrap: wrap;
        gap: 4px 0;
        margin-bottom: 8px;
    }
    nav a {
        color: var(--label-2);
        padding: 0 10px;
        border-inline-start: 0.5px solid var(--separator);
    }
    nav a:first-child { padding-inline-start: 0; border: none; }
    @media (hover: hover) { nav a:hover { color: var(--label); text-decoration: underline; } }
    small { display: block; font-size: 12px; color: var(--label-3); }
`;
